/**
 * Join-flow route guard decisions, as pure data.
 *
 * Kept apart from the guard components so the branches are unit-testable as a
 * table and the component files export only components (react-refresh).
 */
import type { JoinOnboardingStatus } from './api';
import { dashboardPath, joinBasePath } from './constants';

export type JoinRouteDecision =
  | { kind: 'loading' }
  | { kind: 'redirect'; to: string }
  | { kind: 'render' };

/**
 * Decides what a join-flow route should do given the current onboarding status.
 *
 * - Error → redirect to dashboard (can't render the wizard without status)
 * - Loading / no status yet → show loading indicator
 * - Join not required OR already completed → redirect to dashboard
 * - Otherwise → render the join wizard
 */
export function resolveJoinRouteGuard(inputs: {
  orgId: string;
  status: JoinOnboardingStatus | undefined;
  isLoading: boolean;
  isError: boolean;
}): JoinRouteDecision {
  if (inputs.isError) {
    return { kind: 'redirect', to: dashboardPath(inputs.orgId) };
  }
  if (inputs.isLoading || inputs.status === undefined) {
    return { kind: 'loading' };
  }
  if (!inputs.status.required || inputs.status.completed) {
    return { kind: 'redirect', to: dashboardPath(inputs.orgId) };
  }
  return { kind: 'render' };
}

export type DashboardJoinDecision =
  | { kind: 'render' }
  | { kind: 'redirect'; to: string };

/**
 * Decides whether the dashboard should redirect to the join wizard.
 *
 * Redirects ONLY when join onboarding is required and not yet completed.
 * Falls through to render in all other cases (error, loading, not required,
 * already completed).
 */
export function resolveDashboardJoinRedirect(inputs: {
  orgId: string;
  status: JoinOnboardingStatus | undefined;
  isError: boolean;
}): DashboardJoinDecision {
  if (
    !inputs.isError &&
    inputs.status &&
    inputs.status.required &&
    !inputs.status.completed
  ) {
    return { kind: 'redirect', to: joinBasePath(inputs.orgId) };
  }
  return { kind: 'render' };
}
